import { useEffect, useMemo, useState, type ReactNode, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EmailComposer } from "./EmailComposer";
import type { ReplyToType } from "@/lib/jobs";
import { emailTemplatesApi } from "@/lib/email-templates";
import { emailApi, type FromAddress } from "@/lib/email";

export type SendEmailAttachment = {
  id: string;
  name: string;
  source: "computer" | "library";
};

export type SendEmailInlineImage = { cid: string; attachmentId: string };

export type SendEmailPayload = {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  htmlBody: string;
  replyToType: ReplyToType;
  attachmentIds: string[];
  inlineImages: SendEmailInlineImage[];
  fromCode?: string;
};

export interface SendEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTo?: string;
  defaultCc?: string;
  defaultBcc?: string;
  defaultSubject?: string;
  defaultBody?: string;
  defaultReplyToType?: ReplyToType;
  /** Required: uploads a local file and returns an attachment record. */
  onUploadFromComputer: (file: File) => Promise<SendEmailAttachment>;
  /** Optional: when provided, renders "Upload Files from Library" button. */
  onPickFromLibrary?: () => Promise<SendEmailAttachment[]>;
  /** Optional UI rendered to the right of the To field. */
  extraHeaderSlot?: ReactNode;
  /** Optional: when provided, the composer shows an "Insert image" button that uploads inline images. */
  onUploadInlineImage?: (file: File) => Promise<{ cid: string; url: string; attachmentId: string }>;
  /** Optional: shows the From-address select and includes `fromCode` in the send payload.
   * Only set this when the caller's `onSend` actually forwards `fromCode` to the API —
   * currently just `ComposeEmailDialog`. Quote/Job send paths don't honor it, so the
   * select stays hidden there (it would otherwise silently do nothing). Default: false. */
  showFromSelect?: boolean;
  onSend: (payload: SendEmailPayload) => Promise<void>;
}

const REPLY_TO_OPTIONS: { value: ReplyToType; label: string }[] = [
  { value: "CurrentUser", label: "Current user" },
  { value: "RequestingEmployee", label: "Requesting employee" },
  { value: "NoReply", label: "No Reply" },
  { value: "GeneralInbox", label: "General inbox" },
];

const DEFAULT_FROM_CODE = "REQEMPLOYEE";

/** Rendered immediately (before/without the from-addresses query resolving)
 * so the From select always has a selectable default. Matches the seeded
 * REQEMPLOYEE row's description. */
const DEFAULT_FROM_OPTION: FromAddress = {
  code: DEFAULT_FROM_CODE,
  description: "Your email address",
  emailAddress: null,
};

/**
 * Modal email composer used across Job / Quote / Client / Enquiry surfaces.
 * Caller supplies `onSend` (matching their own send mutation) and default
 * values. The dialog stays generic — no surface-specific knowledge.
 *
 * Contract:
 * - Draft state resets every time `open` transitions to `true`.
 *   Caller can mount once and toggle `open`.
 * - `onSend` is awaited; the dialog closes only on successful resolution.
 *   On rejection, the dialog stays open and surfaces the error inline.
 * - Attachments are dialog-owned (default empty). Task 15 wires upload buttons.
 */
export function SendEmailDialog({
  open,
  onOpenChange,
  defaultTo = "",
  defaultCc = "",
  defaultBcc = "",
  defaultSubject = "",
  defaultBody = "",
  defaultReplyToType = "CurrentUser",
  onUploadFromComputer,
  onPickFromLibrary,
  extraHeaderSlot,
  onUploadInlineImage,
  showFromSelect = false,
  onSend,
}: SendEmailDialogProps) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState(defaultCc);
  const [bcc, setBcc] = useState(defaultBcc);
  const [subject, setSubject] = useState(defaultSubject);
  const [htmlBody, setHtmlBody] = useState(defaultBody);
  const [replyToType, setReplyToType] = useState<ReplyToType>(defaultReplyToType);
  const [fromCode, setFromCode] = useState<string>(DEFAULT_FROM_CODE);
  const [attachments, setAttachments] = useState<SendEmailAttachment[]>([]);
  const [inlineImages, setInlineImages] = useState<SendEmailInlineImage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when re-opened with new defaults
  useEffect(() => {
    if (open) {
      setTo(defaultTo);
      setCc(defaultCc);
      setBcc(defaultBcc);
      setSubject(defaultSubject);
      setHtmlBody(defaultBody);
      setReplyToType(defaultReplyToType);
      setFromCode(DEFAULT_FROM_CODE);
      setAttachments([]);
      setInlineImages([]);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // From-address options (Display=true rows: REQEMPLOYEE, SYSTEM, ACCOUNTS, …).
  // Doesn't block the dialog — the select always has DEFAULT_FROM_OPTION
  // available while the query is loading or if it fails.
  const { data: fromAddressesData } = useQuery({
    queryKey: ["email-from-addresses"],
    queryFn: () => emailApi.fromAddresses(),
    enabled: open && showFromSelect,
  });
  const fromOptions = useMemo<FromAddress[]>(() => {
    const list = fromAddressesData ?? [];
    return list.some((f) => f.code === DEFAULT_FROM_CODE) ? list : [DEFAULT_FROM_OPTION, ...list];
  }, [fromAddressesData]);

  async function handleUploadInlineImage(file: File): Promise<{ cid: string; url: string }> {
    if (!onUploadInlineImage) throw new Error("Inline image upload is not supported here.");
    try {
      const { cid, url, attachmentId } = await onUploadInlineImage(file);
      setInlineImages((prev) => [...prev, { cid, attachmentId }]);
      return { cid, url };
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to upload ${file.name}`);
      throw err;
    }
  }

  async function doSubmit() {
    if (!to.trim() || !subject.trim()) {
      setError("To and Subject are required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const doc = new DOMParser().parseFromString(htmlBody, "text/html");
      doc.querySelectorAll("img[data-cid]").forEach((img) => {
        const cid = img.getAttribute("data-cid")!;
        img.setAttribute("src", `cid:${cid}`);
        img.removeAttribute("data-cid");
      });
      const finalBody = doc.body.innerHTML;
      const usedCids = new Set(
        Array.from(doc.querySelectorAll('img[src^="cid:"]')).map((i) => i.getAttribute("src")!.slice(4)),
      );
      const finalInlineImages = inlineImages.filter((im) => usedCids.has(im.cid));

      await onSend({
        to: to.trim(),
        cc: cc.trim() || undefined,
        bcc: bcc.trim() || undefined,
        subject: subject.trim(),
        htmlBody: finalBody,
        replyToType,
        attachmentIds: attachments.map((a) => a.id),
        inlineImages: finalInlineImages,
        ...(showFromSelect ? { fromCode } : {}),
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send email");
    } finally {
      setSubmitting(false);
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void doSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send email</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          {showFromSelect && (
            <div className="space-y-1">
              <Label htmlFor="email-from">From</Label>
              <Select value={fromCode} onValueChange={setFromCode}>
                <SelectTrigger id="email-from" aria-label="From">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fromOptions.map((o) => (
                    <SelectItem key={o.code} value={o.code}>
                      {o.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-end gap-3">
            <div className="flex-1 space-y-1">
              <Label htmlFor="email-to">To</Label>
              <Input id="email-to" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            {extraHeaderSlot && (
              <div className="pb-1">{extraHeaderSlot}</div>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="email-cc">Cc</Label>
            <Input id="email-cc" value={cc} onChange={(e) => setCc(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="email-bcc">Bcc</Label>
            <Input id="email-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="email-subject">Subject</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>Message</Label>
              <TemplatePopover
                onPick={(t) => {
                  if ((subject || htmlBody) && !window.confirm("Replace current Subject and Message?")) return;
                  setSubject(t.subject);
                  setHtmlBody(t.htmlBody);
                }}
              />
            </div>
            <EmailComposer
              value={htmlBody}
              onChange={setHtmlBody}
              disabled={submitting}
              onUploadInlineImage={onUploadInlineImage ? handleUploadInlineImage : undefined}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="email-reply-to">Reply to</Label>
            <Select value={replyToType} onValueChange={(v) => setReplyToType(v as ReplyToType)}>
              <SelectTrigger id="email-reply-to" aria-label="Reply To">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPLY_TO_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Attachments — chips + upload buttons */}
          <div className="space-y-2">
            <Label>Attachments</Label>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((a) => (
                  <Badge key={a.id} variant="outline" className="flex items-center gap-1">
                    {a.name}
                    <button
                      type="button"
                      aria-label={`Remove ${a.name}`}
                      className="ml-1 text-muted-foreground hover:text-foreground"
                      onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Label
                htmlFor="email-upload-computer"
                className="inline-flex cursor-pointer items-center rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent"
              >
                Upload Files from Computer
              </Label>
              <input
                id="email-upload-computer"
                type="file"
                className="sr-only"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  void (async () => {
                    for (const f of files) {
                      try {
                        const att = await onUploadFromComputer(f);
                        setAttachments((prev) => [...prev, att]);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : `Failed to upload ${f.name}`);
                      }
                    }
                  })();
                }}
              />
              {onPickFromLibrary && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void (async () => {
                      try {
                        const picked = await onPickFromLibrary();
                        setAttachments((prev) => [...prev, ...picked]);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : "Failed to pick from library");
                      }
                    })();
                  }}
                >
                  Upload Files from Library
                </Button>
              )}
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TemplatePopover({
  onPick,
}: {
  onPick: (t: { subject: string; htmlBody: string }) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () => emailTemplatesApi.list(0, 50, false),
    select: (r) => r.items,
  });
  const items = data ?? [];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          Copy Template
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No templates yet. Create one in Admin → Email Templates.
          </p>
        ) : (
          <ul className="space-y-1">
            {items.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => onPick({ subject: t.subject, htmlBody: t.htmlBody })}
                  className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-accent"
                >
                  {t.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
